/**
 * Keyboard Shortcuts Dialog Component
 * WCAG 2.1 AA compliant dialog showing available keyboard shortcuts.
 * @packageDocumentation
 */

import { useTranslation } from 'react-i18next';
import { useRef, useMemo, useEffect, useCallback } from 'react';

import {
  formatShortcut,
  GLOBAL_SHORTCUTS,
  type KeyboardShortcut,
  useKeyboardShortcutsHelp,
} from '@/hooks/useKeyboardShortcuts';

import { Button } from './button';
import { Dialog, DialogClose, DialogTitle, DialogHeader, DialogContent, DialogDescription } from './dialog';

interface ShortcutGroup {
  scope: string;
  shortcuts: KeyboardShortcut[];
}

const groupShortcuts = (shortcuts: KeyboardShortcut[]): ShortcutGroup[] => {
  const groups = new Map<string, KeyboardShortcut[]>();

  for (const shortcut of shortcuts) {
    const scope = shortcut.scope ?? 'General';
    const existing = groups.get(scope) ?? [];
    existing.push(shortcut);
    groups.set(scope, existing);
  }

  return Array.from(groups.entries())
    .map(([scope, shortcuts]) => ({
      scope,
      shortcuts,
    }))
    .sort((a, b) => {
      if (a.scope === 'global') {
        return -1;
      }

      if (b.scope === 'global') {
        return 1;
      }

      return a.scope.localeCompare(b.scope);
    });
};

const SCOPE_LABEL_KEYS: Record<string, string> = {
  global: 'shortcuts.scope.global',
  editor: 'shortcuts.scope.editor',
  navigation: 'shortcuts.scope.navigation',
};

/**
 * Dialog component that displays all available keyboard shortcuts grouped by scope.
 *
 * @returns { JSX.Element } The rendered keyboard shortcuts dialog.
 */
export const KeyboardShortcutsDialog = () => {
  const { t } = useTranslation();
  const { close, isOpen } = useKeyboardShortcutsHelp();
  const groupedShortcuts = useMemo(() => groupShortcuts(GLOBAL_SHORTCUTS), []);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
      }
    },
    [close],
  );

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby="keyboard-shortcuts-description">
        <DialogHeader>
          <DialogTitle>{t('shortcuts.title')}</DialogTitle>
          <DialogDescription id="keyboard-shortcuts-description">{t('shortcuts.description')}</DialogDescription>
        </DialogHeader>

        <div className="max-h-96 space-y-6 overflow-y-auto py-4">
          {groupedShortcuts.map(({ scope, shortcuts }) => (
            <section key={scope} aria-labelledby={`shortcuts-${scope}`}>
              <h3 id={`shortcuts-${scope}`} className="mb-3 text-sm font-medium text-muted-foreground">
                {SCOPE_LABEL_KEYS[scope] ? t(SCOPE_LABEL_KEYS[scope]) : scope}
              </h3>
              <dl className="space-y-2">
                {shortcuts.map((shortcut) => (
                  <div key={shortcut.key} className="flex items-center justify-between">
                    <dt className="text-sm">{shortcut.description}</dt>
                    <dd>
                      <kbd className="inline-flex items-center gap-1 rounded border bg-muted px-2 py-1 font-mono text-xs">
                        {formatShortcut(shortcut)}
                      </kbd>
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <div className="flex items-center justify-between border-t pt-4">
          <p className="text-xs text-muted-foreground">
            Press{' '}
            <kbd className="rounded bg-muted px-1 py-0.5 text-xs">
              {typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent) ? '\u2318' : 'Ctrl'}
              +/
            </kbd>{' '}
            {t('shortcuts.toggleHint')}
          </p>
          <DialogClose asChild>
            <Button ref={closeButtonRef} variant="outline" size="sm">
              {t('actions.close')}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
};
